import React, { useState } from 'react';
import type { VideoVersion } from './videoStudioTypes';
import { downloadVideoFile } from './videoDownload';

interface VideoVersionItemProps {
  version: VideoVersion;
  index: number;
  isSelected: boolean;
  isSubmitting: boolean;
  onSelect: (versionId: string, url: string) => void;
}

export const VideoVersionItem: React.FC<VideoVersionItemProps> = ({
  version,
  index,
  isSelected,
  isSubmitting,
  onSelect,
}) => {
  const [downloading, setDownloading] = useState<'mp4' | 'webm' | null>(null);
  const [progress, setProgress] = useState<string>('');

  const handleDownload = async (format: 'mp4' | 'webm') => {
    try {
      setDownloading(format);
      await downloadVideoFile(version.video_url, {
        filename: `video_v${index}.${format}`,
        format,
        onProgress: (p) => setProgress(p),
      });
    } catch (err) {
      console.error('Erro ao baixar vídeo:', err);
    } finally {
      setDownloading(null);
      setProgress('');
    }
  };

  return (
    <div className={`vstudio-version-item ${isSelected ? 'selected' : ''}`}>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <div style={{ width: '80px', height: '110px', borderRadius: '0.5rem', overflow: 'hidden', background: '#000', flexShrink: 0 }}>
          <video
            src={version.video_url}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            poster={version.thumbnail_url || undefined}
          />
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Versão #{index}</span>
            <span className={`vstudio-badge ${isSelected ? 'vstudio-badge-success' : 'vstudio-badge-secondary'}`}>
              {isSelected ? '★ Vídeo Oficial Ativo' : 'Candidato'}
            </span>
          </div>

          <p style={{ margin: '0 0 0.35rem 0', fontSize: '0.75rem', color: 'var(--text-secondary, #64748b)' }}>
            Fonte: {version.source_type} • Duração: {version.duration_seconds || 15}s • {new Date((version.created_at || 0) * 1000).toLocaleString('pt-BR')}
          </p>

          {version.review_notes && (
            <p style={{ margin: 0, fontSize: '0.8rem', fontStyle: 'italic', color: 'var(--text-secondary, #64748b)' }}>
              "{version.review_notes}"
            </p>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            type="button"
            disabled={!!downloading}
            onClick={() => handleDownload('mp4')}
            className="vstudio-btn vstudio-btn-outline"
            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', color: '#10b981', borderColor: '#10b98140', cursor: downloading ? 'wait' : 'pointer' }}
          >
            {downloading === 'mp4' ? (progress || 'Baixando...') : 'Baixar MP4'}
          </button>
          <button
            type="button"
            disabled={!!downloading}
            onClick={() => handleDownload('webm')}
            className="vstudio-btn vstudio-btn-outline"
            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', cursor: downloading ? 'wait' : 'pointer' }}
          >
            {downloading === 'webm' ? (progress || 'Baixando...') : 'WebM'}
          </button>
        </div>

        {!isSelected ? (
          <button
            type="button"
            className="vstudio-btn vstudio-btn-primary"
            style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
            onClick={() => onSelect(version.id, version.video_url)}
            disabled={isSubmitting}
          >
            ✓ Escolher como Oficial
          </button>
        ) : (
          <span style={{ fontSize: '0.8rem', color: '#16a34a', fontWeight: 600 }}>
            ✓ Em uso na campanha
          </span>
        )}
      </div>
    </div>
  );
};
